import React, { useMemo } from 'react';
import { Filter, Calendar, Package, MapPin, RefreshCw, Copy, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const SummaryPeriodTab = ({
  summaryPeriod,
  setSummaryPeriod,
  summaryData,
  summarySupplier,
  setSummarySupplier,
  summaryProduct,
  setSummaryProduct,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  viewMode,
  setViewMode,
  roundingMode,
  setRoundingMode,
  loading,
  loadSummaryByPeriod,
  setDebugDialogOpen
}) => {
  // Suppliers from summary
  const summarySuppliers = useMemo(() => {
    if (!summaryData?.by_product) return [];
    return [...new Set(summaryData.by_product.map(p => p.supplier || 'Sin asignar'))].sort();
  }, [summaryData]);

  // Products list for filter
  const summaryProductsList = useMemo(() => {
    if (!summaryData?.by_product) return [];
    let products = summaryData.by_product;
    if (summarySupplier !== 'all') {
      products = products.filter(p => (p.supplier || 'Sin asignar') === summarySupplier);
    }
    return products.map(p => ({ id: p.item_id, name: p.item_name, code: p.item_code }));
  }, [summaryData, summarySupplier]);

  // Filtered products
  const filteredSummaryProducts = useMemo(() => {
    if (!summaryData?.by_product) return [];
    let filtered = summaryData.by_product;
    
    if (summarySupplier !== 'all') {
      filtered = filtered.filter(p => (p.supplier || 'Sin asignar') === summarySupplier);
    }
    
    if (summaryProduct !== 'all') {
      filtered = filtered.filter(p => p.item_id === summaryProduct);
    }
    
    return filtered;
  }, [summaryData, summarySupplier, summaryProduct]);

  // Stats based on filters
  const filteredSummaryStats = useMemo(() => {
    if (!summaryData) return { total_quantity: 0, total_products: 0, total_locations: 0 };
    
    if (summarySupplier === 'all' && summaryProduct === 'all') {
      return summaryData.totals;
    }
    
    const totalQuantity = filteredSummaryProducts.reduce((sum, p) => sum + p.total_quantity, 0);
    const totalProducts = filteredSummaryProducts.length;
    
    const uniqueLocations = new Set();
    filteredSummaryProducts.forEach(p => {
      if (p.location_breakdown) {
        Object.keys(p.location_breakdown).forEach(loc => uniqueLocations.add(loc));
      }
    });
    
    return {
      total_quantity: totalQuantity,
      total_products: totalProducts,
      total_locations: uniqueLocations.size
    };
  }, [summaryData, summarySupplier, summaryProduct, filteredSummaryProducts]);

  // Filtered by location
  const filteredByLocation = useMemo(() => {
    if (!summaryData) return [];
    
    if (summarySupplier === 'all' && summaryProduct === 'all') {
      return summaryData.by_location;
    }
    
    const locationTotals = {};
    filteredSummaryProducts.forEach(product => {
      if (product.location_breakdown) {
        Object.entries(product.location_breakdown).forEach(([locName, qty]) => {
          if (!locationTotals[locName]) {
            locationTotals[locName] = {
              location_name: locName,
              total_items: 0,
              unique_products: new Set()
            };
          }
          locationTotals[locName].total_items += qty;
          locationTotals[locName].unique_products.add(product.item_id);
        });
      }
    });
    
    return Object.values(locationTotals).map(loc => ({
      location_name: loc.location_name,
      total_items: loc.total_items,
      unique_products_count: loc.unique_products.size
    })).sort((a, b) => b.total_items - a.total_items);
  }, [summaryData, summarySupplier, summaryProduct, filteredSummaryProducts]);

  // Copy to clipboard
  const copySummaryToClipboard = () => {
    if (!summaryData || filteredSummaryProducts.length === 0) {
      toast.error('No hay datos para copiar');
      return;
    }

    const supplierName = summarySupplier === 'all' ? 'Todos' : summarySupplier;
    
    let content = `*RESUMEN DE PEDIDOS*\n`;
    content += `${summaryData.period_name}\n`;
    content += `${summaryData.start_date} al ${summaryData.end_date}\n`;
    if (summarySupplier !== 'all') {
      content += `Proveedor: ${supplierName}\n`;
    }
    content += `\n`;

    const bySupplier = {};
    filteredSummaryProducts.forEach(p => {
      const sup = p.supplier || 'Sin asignar';
      if (!bySupplier[sup]) bySupplier[sup] = [];
      bySupplier[sup].push(p);
    });

    Object.entries(bySupplier).forEach(([supplier, products]) => {
      content += `*${supplier}*\n`;
      products.forEach(p => {
        const unidadesPorCaja = p.unidades_por_caja;
        const unidadCompra = p.unidad_compra || 'caja';
        
        if (viewMode === 'boxes' && unidadesPorCaja && unidadesPorCaja > 0) {
          const cajas = roundingMode === 'ceil' ? p.cajas_sugeridas : p.cajas_exactas;
          content += `• ${p.item_name}: *${cajas} ${unidadCompra}${cajas !== 1 ? 's' : ''}*\n`;
        } else {
          content += `• ${p.item_name}: *${p.total_quantity} ${p.unit}*\n`;
        }
      });
      content += `\n`;
    });

    navigator.clipboard.writeText(content).then(() => {
      toast.success('Resumen copiado al portapapeles');
    }).catch(() => {
      toast.error('Error al copiar');
    });
  };

  const handleSupplierChange = (value) => {
    setSummarySupplier(value);
    setSummaryProduct('all');
  };

  return (
    <div className="space-y-6" data-testid="summary-period-tab">
      {/* Controls */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Período</label>
            <Select value={summaryPeriod} onValueChange={setSummaryPeriod}>
              <SelectTrigger className="w-52" data-testid="summary-period-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7days">Últimos 7 días</SelectItem>
                <SelectItem value="30days">Últimos 30 días</SelectItem>
                <SelectItem value="thismonth">Este mes</SelectItem>
                <SelectItem value="lastmonth">Mes anterior</SelectItem>
                <SelectItem value="week">Esta semana (Lun-Dom)</SelectItem>
                <SelectItem value="workweek">Esta semana (Lun-Vie)</SelectItem>
                <SelectItem value="custom">Rango personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {summaryPeriod === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Desde</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-44"
                  data-testid="custom-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Hasta</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-44"
                  data-testid="custom-end-date"
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Filtrar por Proveedor</label>
            <Select value={summarySupplier} onValueChange={handleSupplierChange}>
              <SelectTrigger className="w-52" data-testid="summary-supplier-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Todos los proveedores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {summarySuppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Filtrar por Producto</label>
            <Select value={summaryProduct} onValueChange={setSummaryProduct}>
              <SelectTrigger className="w-64" data-testid="summary-product-filter">
                <Package className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Todos los productos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los productos</SelectItem>
                {summaryProductsList.map(product => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.code} - {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => loadSummaryByPeriod()} 
            disabled={loading || (summaryPeriod === 'custom' && (!customStartDate || !customEndDate))}
            data-testid="update-summary-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button 
            variant="outline"
            onClick={copySummaryToClipboard} 
            disabled={!summaryData || filteredSummaryProducts.length === 0}
            title="Copiar resumen al portapapeles"
            data-testid="copy-summary-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copiar
          </Button>
          <Button 
            variant="outline"
            onClick={() => setDebugDialogOpen(true)}
            title="Diagnosticar pedidos de un producto"
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            data-testid="debug-btn"
          >
            <Bug className="w-4 h-4 mr-2" />
            Diagnosticar
          </Button>
        </div>
        
        {summaryData && (
          <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
            <div className="flex flex-wrap items-center gap-4">
              <span><span className="font-medium">{summaryData.period_name}</span>: {summaryData.start_date} al {summaryData.end_date}</span>
              {summaryData.debug && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                  DEBUG: {summaryData.debug.valid_orders_in_range} pedidos contados 
                  {summaryData.debug.orders_not_counted > 0 && (
                    <span className="text-red-600 ml-1">
                      ({summaryData.debug.orders_not_counted} sin contar)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : summaryData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-5 shadow-lg">
              <p className="text-sm opacity-90">Total Unidades Pedidas</p>
              <p className="text-3xl font-bold">{filteredSummaryStats.total_quantity}</p>
              {(summarySupplier !== 'all' || summaryProduct !== 'all') && (
                <p className="text-xs opacity-75 mt-1">(filtrado)</p>
              )}
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-5 shadow-lg">
              <p className="text-sm opacity-90">Productos Diferentes</p>
              <p className="text-3xl font-bold">{filteredSummaryStats.total_products}</p>
              {(summarySupplier !== 'all' || summaryProduct !== 'all') && (
                <p className="text-xs opacity-75 mt-1">(filtrado)</p>
              )}
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-5 shadow-lg">
              <p className="text-sm opacity-90">Locales Activos</p>
              <p className="text-3xl font-bold">{filteredSummaryStats.total_locations}</p>
              {(summarySupplier !== 'all' || summaryProduct !== 'all') && (
                <p className="text-xs opacity-75 mt-1">(filtrado)</p>
              )}
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg p-5 shadow-lg">
              <p className="text-sm opacity-90">Proveedores</p>
              <p className="text-3xl font-bold">
                {summarySupplier !== 'all' ? 1 : summarySuppliers.length}
              </p>
            </div>
          </div>

          {/* By Location */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Pedidos por Local
                {(summarySupplier !== 'all' || summaryProduct !== 'all') && (
                  <span className="text-xs font-normal text-slate-500">(filtrado)</span>
                )}
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {filteredByLocation.map((loc, index) => (
                  <motion.div
                    key={loc.location_name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                  >
                    <h4 className="font-semibold text-slate-900">{loc.location_name}</h4>
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Total pedido:</span>
                        <span className="font-bold text-primary">{loc.total_items} unidades</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Productos únicos:</span>
                        <span className="font-medium">{loc.unique_products_count}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Products Detail Table */}
          <SummaryProductsTable
            summaryData={summaryData}
            filteredSummaryProducts={filteredSummaryProducts}
            viewMode={viewMode}
            setViewMode={setViewMode}
            roundingMode={roundingMode}
            setRoundingMode={setRoundingMode}
          />
        </>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
          <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-muted-foreground">Selecciona un período para ver el resumen</p>
        </div>
      )}
    </div>
  );
};

// Subcomponent for the products table
const SummaryProductsTable = ({
  summaryData,
  filteredSummaryProducts,
  viewMode,
  setViewMode,
  roundingMode,
  setRoundingMode
}) => {
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Detalle por Producto ({filteredSummaryProducts.length} productos)
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Vista:</span>
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-32 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="units">Unidades</SelectItem>
                  <SelectItem value="boxes">Cajas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {viewMode === 'boxes' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Cálculo:</span>
                <Select value={roundingMode} onValueChange={setRoundingMode}>
                  <SelectTrigger className="w-36 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact">Exacto</SelectItem>
                    <SelectItem value="ceil">Redondeado ↑</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Código</th>
              <th className="px-4 py-3 text-left font-semibold">Producto</th>
              <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
              <th className="px-4 py-3 text-right font-semibold bg-green-50">Stock Bodega</th>
              <th className="px-4 py-3 text-right font-semibold">Total Período</th>
              <th className="px-4 py-3 text-right font-semibold bg-orange-50">A Pedir</th>
              {viewMode === 'boxes' && (
                <>
                  <th className="px-4 py-3 text-center font-semibold bg-amber-50">Un/Caja</th>
                  <th className="px-4 py-3 text-right font-semibold bg-amber-50">
                    {roundingMode === 'ceil' ? 'Cajas a Pedir' : 'Cajas (exacto)'}
                  </th>
                </>
              )}
              {summaryData.by_location.map(loc => (
                <th key={loc.location_name} className="px-4 py-3 text-right font-semibold bg-blue-50">
                  {loc.location_name.replace('Local ', '')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSummaryProducts.map((product, index) => {
              const stock = product.stock_bodega_central ?? 0;
              const totalPedido = product.total_quantity ?? 0;
              const aPedir = Math.max(0, totalPedido - stock);
              const stockSuficiente = stock >= totalPedido;
              const unidadesPorCaja = product.unidades_por_caja;
              const unidadCompra = product.unidad_compra || 'unidad';
              const cajasExactas = product.cajas_exactas;
              const cajasSugeridas = product.cajas_sugeridas;
              const cajasDisplay = roundingMode === 'ceil' ? cajasSugeridas : cajasExactas;
              const hasDaily = product.daily_breakdown && product.daily_breakdown.length > 0;
              
              return (
                <React.Fragment key={product.item_id}>
                  <tr className={`border-b hover:bg-slate-50 ${index % 2 === 0 ? '' : 'bg-slate-25'}`}>
                    <td className="px-4 py-3 font-mono text-sm">{product.item_code}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{product.item_name}</span>
                        {hasDaily && (
                          <button
                            onClick={() => {
                              const el = document.getElementById(`detail-${product.item_id}`);
                              if (el) el.classList.toggle('hidden');
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                            title="Ver desglose por día"
                          >
                            [{product.daily_breakdown.length} pedidos]
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md">
                        {product.supplier}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold bg-green-50/50 ${stockSuficiente ? 'text-green-700' : 'text-red-600'}`}>
                      {stock} {product.unit}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-primary">
                      {totalPedido} {product.unit}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold bg-orange-50/50 ${aPedir > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {aPedir > 0 ? aPedir : '✓'} {aPedir > 0 ? product.unit : ''}
                    </td>
                    {viewMode === 'boxes' && (
                      <>
                        <td className="px-4 py-3 text-center bg-amber-50/50 text-slate-600">
                          {unidadesPorCaja ? (
                            <span className="text-sm">{unidadesPorCaja}</span>
                          ) : (
                            <span className="text-xs text-slate-400">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-bold bg-amber-50/50">
                          {cajasDisplay !== null && cajasDisplay !== undefined ? (
                            <span className="text-amber-700">
                              {cajasDisplay} {unidadCompra !== 'unidad' ? unidadCompra : 'cajas'}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Sin config.</span>
                          )}
                        </td>
                      </>
                    )}
                    {summaryData.by_location.map(loc => (
                      <td key={loc.location_name} className="px-4 py-3 text-right bg-blue-50/50">
                        {product.location_breakdown[loc.location_name] || '-'}
                      </td>
                    ))}
                  </tr>
                  {/* Expandable detail row */}
                  {hasDaily && (
                    <tr id={`detail-${product.item_id}`} className="hidden bg-slate-50">
                      <td colSpan={viewMode === 'boxes' ? 8 + summaryData.by_location.length : 6 + summaryData.by_location.length} className="px-4 py-2">
                        <div className="text-xs space-y-1">
                          <div className="font-semibold text-slate-600 mb-2">Detalle de pedidos:</div>
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            {product.daily_breakdown.map((d, i) => (
                              <div key={i} className="bg-white px-2 py-1 rounded border border-slate-200">
                                <div className="text-slate-500">{d.date}</div>
                                <div className="font-medium">{d.location}</div>
                                <div className="text-primary font-bold">{d.quantity} {product.unit}</div>
                                {d.order_type === 'barra' && (
                                  <span className="text-xs text-purple-600">(Barra)</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100 font-bold">
            <tr>
              <td colSpan="3" className="px-4 py-3 text-right">TOTAL:</td>
              <td className="px-4 py-3 text-right bg-green-50/50 text-green-700">
                {filteredSummaryProducts.reduce((sum, p) => sum + (p.stock_bodega_central ?? 0), 0)} unidades
              </td>
              <td className="px-4 py-3 text-right text-lg text-primary">
                {filteredSummaryProducts.reduce((sum, p) => sum + p.total_quantity, 0)} unidades
              </td>
              <td className="px-4 py-3 text-right bg-orange-50/50 text-orange-600">
                {filteredSummaryProducts.reduce((sum, p) => {
                  const stock = p.stock_bodega_central ?? 0;
                  const total = p.total_quantity ?? 0;
                  return sum + Math.max(0, total - stock);
                }, 0)} unidades
              </td>
              {viewMode === 'boxes' && (
                <>
                  <td className="px-4 py-3 text-center bg-amber-50/50">-</td>
                  <td className="px-4 py-3 text-right bg-amber-50/50 text-amber-700">
                    {filteredSummaryProducts.reduce((sum, p) => {
                      const cajas = roundingMode === 'ceil' ? p.cajas_sugeridas : p.cajas_exactas;
                      return sum + (cajas || 0);
                    }, 0)} cajas
                  </td>
                </>
              )}
              {summaryData.by_location.map(loc => (
                <td key={loc.location_name} className="px-4 py-3 text-right bg-blue-50/50">
                  {filteredSummaryProducts.reduce((sum, p) => sum + (p.location_breakdown[loc.location_name] || 0), 0)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SummaryPeriodTab;
